import type { HTMLAttributes, PropsWithChildren } from 'react';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import type { ImageValue } from '@elfsight/configurator-contracts';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { MediaImage } from '@elfsight/widget-media';
import { getOptimizedImageLink } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';

type LocationPreviewImageLayoutProps = HTMLAttributes<HTMLDivElement> &
  PropsWithChildren<{
    hoverState?: boolean;
    image?: ImageValue;
    alt?: string;
  }>;

const IMAGE_SIZE = 270;

export const LocationPreviewImageLayout = forwardRef<
  HTMLDivElement,
  LocationPreviewImageLayoutProps
>(({ children, image, alt, hoverState, ...forwardingProps }, ref) => {
  const { previewCardStyle, textColor, previewCardSideImageAspectRatio } =
    useSettings();

  if (!image || previewCardStyle === 'no-image') {
    return (
      <Container
        _hoverState={hoverState}
        _direction={previewCardStyle === 'image-right' ? 'row-reverse' : 'row'}
        ref={ref}
        {...forwardingProps}
      >
        <Content>{children}</Content>
      </Container>
    );
  }

  if (['image-left', 'image-right'].includes(previewCardStyle)) {
    return (
      <Container
        ref={ref}
        _hoverState={hoverState}
        _direction={previewCardStyle === 'image-right' ? 'row-reverse' : 'row'}
        {...forwardingProps}
      >
        <StyledMediaImage
          src={getOptimizedImageLink(image.url, {
            width: IMAGE_SIZE
          })}
          ratio={previewCardSideImageAspectRatio}
          alt={alt}
          withSkeleton
          skeletonColor={getColorWithAlpha(textColor, 0.1)}
          containerWidth="90px"
        />
        <Content>{children}</Content>
      </Container>
    );
  }

  return (
    <Container
      ref={ref}
      _hoverState={hoverState}
      _direction="column"
      _extraBottomPadding
      {...forwardingProps}
    >
      <StyledMediaImage
        src={image.url}
        ratio={0.5625}
        alt={alt}
        withSkeleton
        skeletonColor={getColorWithAlpha(textColor, 0.1)}
      />
      <Content>{children}</Content>
    </Container>
  );
});

const StyledMediaImage = styled(MediaImage)`
  border-radius: 12px;
  flex-shrink: 0;

  img {
    transition: transform 0.3s;
  }
`;

const Container = styled.div<{
  _direction: 'row' | 'row-reverse' | 'column';
  _extraBottomPadding?: boolean;
  _hoverState?: boolean;
}>`
  box-sizing: border-box;
  padding: 12px;
  min-height: 100px;
  padding-bottom: ${({ _extraBottomPadding }) =>
    _extraBottomPadding ? 32 : 12}px;
  transition: background-color 0.3s;
  display: flex;
  flex-direction: ${({ _direction }) => _direction};
  gap: 12px;

  ${({ _hoverState = false }) =>
    _hoverState &&
    css`
      background: rgba(17, 17, 17, 0.05);
      ${StyledMediaImage} img {
        transform: scale(1.05);
      }
    `}

  &:hover {
    background: rgba(17, 17, 17, 0.05);
  }
  &:hover ${StyledMediaImage} img {
    transform: scale(1.05);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 8px;
  align-items: flex-start;
  min-width: 0;
  overflow: hidden;
`;
